// ScheduleFollowUp.tsx
import React from "react";
import { Button, Form, Row, Col, Spinner } from "react-bootstrap";
import TextEditor from "components/TextEditor/TextEditor";
import { MecordColors } from "Constants/Color";
import { Autocomplete, TextField } from "@mui/material";
import { autocompleteDropdownStyles } from "Constants/Style";

// Props and Interface definitions (copied from parent)
interface FollowUpData {
  followUpType: string;
  dateTime: string;
  nextFollowUpDate?: string;
}

interface ScheduleFollowUpProps {
  followUpData: FollowUpData;
  editorContent: string;
  isSubmitting: boolean;
  HARDCODED_FOLLOW_UP_TYPES: { id: string; name: string }[];
  minFollowUpDateTime?: string; // datetime-local min (local time)
  handleInputChange : (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => void;
  setEditorContent: React.Dispatch<React.SetStateAction<string>>;
  handleSubmitFollowUp: (e: React.FormEvent) => Promise<void>;
  onCancel?: () => void;
  editorResetKey?: number;
}

const ScheduleFollowUp: React.FC<ScheduleFollowUpProps> = ({
  followUpData,
  editorContent,
  isSubmitting,
  HARDCODED_FOLLOW_UP_TYPES,
  minFollowUpDateTime,
  handleInputChange,
  setEditorContent,
  handleSubmitFollowUp,
  onCancel,
  editorResetKey = 0,
}) => {
  const labelStyle = {
    fontSize: "13px",
    fontWeight: "600",
    color: "#2c3e50",
    marginBottom: "8px",
    display: "block",
  };

  const requiredLabelStyle = {
    ...labelStyle,
    color: "#2c3e50",
  };

  const asteriskStyle = {
    color: "#ea6666",
    marginLeft: "4px",
    fontWeight: "bold",
  };

  const inputStyle = {
    border: "1px solid #e0e0e0",
    fontSize: "13px",
    padding: "8px 12px",
    borderRadius: "8px",
    width: "100%",
    backgroundColor: "#fff",
    height: "38px",
    transition: "all 0.2s ease",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
  };

  const getMinNextDate = () => {
  if (!followUpData.dateTime) return undefined;
  return followUpData.dateTime.split("T")[0]; // YYYY-MM-DD
};

  return (
    <div>
      <Form onSubmit={handleSubmitFollowUp}>
        <div
          className="p-4 mb-4"
          style={{
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            border: "1px solid #e8ecef",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
          }}
        >
          <Row className="g-3">
            <Col md={12} lg={4}>
              <label style={requiredLabelStyle}>
                Follow-up Type<span style={asteriskStyle}>*</span>
              </label>
              <Autocomplete
                options={HARDCODED_FOLLOW_UP_TYPES}
                getOptionLabel={(option) => option.name}
                value={
                  HARDCODED_FOLLOW_UP_TYPES.find(
                    (type) => type.id === followUpData.followUpType
                  ) || null
                }
                onChange={(event, newValue) => {
                  const eventMock = {
                    target: {
                      name: "followUpType",
                      value: newValue ? newValue.id : "",
                    },
                  } as React.ChangeEvent<HTMLSelectElement>;
                  handleInputChange(eventMock);
                }}
                disabled={isSubmitting}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.name
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select"
                    variant="outlined"
                    size="small"
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#fff",
                        height: "32px",
                        fontSize: "13px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#ced4da",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: MecordColors.PrimaryButtonBGColor,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: MecordColors.PrimaryButtonBGColor,
                      },
                    }}
                  />
                )}
              />
            </Col>
            <Col md={6} lg={4}>
              <label style={{ ...requiredLabelStyle, whiteSpace: "nowrap" }}>
                Follow up Date<span style={asteriskStyle}>*</span>
              </label>
              <Form.Control
                type="datetime-local"
                name="dateTime"
                value={followUpData.dateTime}
                onChange={handleInputChange as any}
                min={minFollowUpDateTime}
                disabled={isSubmitting}
                style={inputStyle}
                onFocus={(e) => {
                  e.target.style.borderColor = "#ea6666";
                  e.target.style.borderWidth = "2px";
                  e.target.style.boxShadow = "0 0 0 3px rgba(234, 102, 102, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e0e0e0";
                  e.target.style.borderWidth = "1px";
                  e.target.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.05)";
                }}
              />
            </Col>
            <Col md={6} lg={4}>
              <label style={{ ...labelStyle, whiteSpace: "nowrap" }}>
                Next Follow Up Date
              </label>
              <Form.Control
                type="date"
                name="nextFollowUpDate"
                 min={getMinNextDate()}
                value={followUpData.nextFollowUpDate || ""}
                onChange={handleInputChange as any}
                disabled={isSubmitting}
                style={inputStyle}
              />
            </Col>
          </Row>
        </div>

        {/* Comment Editor */}
        <div
          className="mb-4"
          style={{
            padding: "16px",
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            border: "1px solid #e8ecef",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
          }}
        >
          <label style={requiredLabelStyle} className="mb-3">
            Remark<span style={asteriskStyle}>*</span>
          </label>
          <TextEditor
            key={`editor-${editorResetKey}`}
            value={editorContent}
            onChange={setEditorContent}
            placeholder="Enter follow-up remarks (supports bold, italic, lists, etc.)"
            minHeightPx={100}
            maxHeightPx={180}
            blockImages={true}
          />
        </div>

        <div
          className="d-flex gap-3 justify-content-end"
          style={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "#ffffff",
            paddingTop: "16px",
            paddingBottom: "8px",
            marginTop: "16px",
            borderTop: "1px solid #e8ecef",
            zIndex: 10,
          }}
        >
          <Button
            type="button"
            variant="light"
            size="sm"
            onClick={onCancel}
            disabled={isSubmitting}
            style={{
              backgroundColor: "#f8f9fa",
              borderColor: "#dee2e6",
              color: "#6c757d",
              fontWeight: "600",
              padding: "8px 20px",
              fontSize: "13px",
              height: "40px",
              borderRadius: "8px",
              transition: "all 0.2s ease",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = "#e9ecef";
              e.currentTarget.style.borderColor = "#ced4da";
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = "0 2px 6px rgba(0, 0, 0, 0.15)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = "#f8f9fa";
              e.currentTarget.style.borderColor = "#dee2e6";
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.1)";
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={isSubmitting}
            style={{
              backgroundColor: "#ea6666",
              borderColor: "#ea6666",
              color: "#fff",
              fontWeight: "600",
              padding: "8px 24px",
              fontSize: "13px",
              height: "40px",
              borderRadius: "8px",
              transition: "all 0.2s ease",
              boxShadow: "0 2px 6px rgba(234, 102, 102, 0.3)",
            }}
            onMouseOver={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = "#d85555";
                e.currentTarget.style.borderColor = "#d85555";
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(234, 102, 102, 0.4)";
              }
            }}
            onMouseOut={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = "#ea6666";
                e.currentTarget.style.borderColor = "#ea6666";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 6px rgba(234, 102, 102, 0.3)";
              }
            }}
          >
            {isSubmitting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                  style={{ borderWidth: "2px" }}
                />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default ScheduleFollowUp;
